import { OtherTheme, Theme } from '@api/api/earnings-insights';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { RatingPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './ThematicFocusCard.module.scss';
import { isTheme } from './utils';

const themeTypeColorClassMap = {
  Investment: semanticColorStyles.themeIndustry,
  Financial: semanticColorStyles.themeFinancial,
  Macro: semanticColorStyles.themeMacro,
};

export interface ThematicFocusCardProps {
  theme: Theme | OtherTheme;
  source: PosthogTrackingSource;
  isLast?: boolean;
  withPill?: boolean;
}

export const ThematicFocusCard = ({
  theme,
  source,
  isLast = false,
  withPill = false,
}: ThematicFocusCardProps) => {
  const { t } = useTranslation(['earnings']);
  const { navigateToTheme } = useNavigateToThemeOverview();

  const isExistingTheme = isTheme(theme);
  const themeId = isExistingTheme ? theme.theme_id : '';
  const { isFetching, isError, error, data } = useTheme({ themeId });
  const themeName = isExistingTheme ? (data?.name ?? '') : theme.name;
  const themeType = isExistingTheme
    ? t('earnings:earningsInsight.drawer.investmentTheme')
    : t(`earnings:earningsInsight.drawer.themeType.${theme.type}`);
  const keyThemeType = isExistingTheme ? 'Investment' : theme.type;

  if (isError || (isExistingTheme && !data && !isFetching)) {
    throw new Error(error?.message);
  }

  const handleClick = () => {
    if (isExistingTheme) {
      navigateToTheme({ themeId, source });
    }
  };

  const getContent = () => {
    if (isFetching) {
      return (
        <div className={styles.skeletonLoaderWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: {
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                },
              },
            ]}
          />
        </div>
      );
    } else {
      return (
        <>
          <div className={styles.headerWrapper}>
            <div className={styles.headerLeftWrapper}>
              {isExistingTheme && (
                <ThemeLogoIcon
                  name={themeName}
                  logo_url={data?.logo_url}
                  type={data?.type ?? 'industry'}
                  size={40}
                />
              )}
              <div className={styles.typeAndTitleWrapper}>
                <div
                  className={clsx(
                    styles.typeLabel,
                    themeTypeColorClassMap[keyThemeType]
                  )}
                >
                  {themeType}
                </div>
                <div className={styles.titleLabel}>{themeName}</div>
              </div>
            </div>
            {withPill && <RatingPill rating={theme.rating} />}
          </div>
          <div className={styles.relationLabel}>{theme.relationship}</div>
        </>
      );
    }
  };

  return (
    <div
      className={clsx(
        styles.root,
        isLast && styles.rootLast,
        isExistingTheme && styles.rootExistingTheme
      )}
      onClick={handleClick}
    >
      {getContent()}
    </div>
  );
};
